import type { ComponentType, ReactNode } from "react";
import type { TFunction } from "i18next";
import type { CampaignSchema } from "@/types";
import type { AppStore } from "@/stores/app.store";
import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import type { RingState } from "@/igw-button/ring";
import { campaignKey, type CampaignKey } from "./campaign-key";
import { isWithinDisplayWindow } from "./campaign-status";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";

/** The FE campaign-type discriminant (mirrors the backend `campaign.type`). */
export type CampaignTypeId =
  | "tournament"
  | "prizedrop"
  | "missions"
  | "sprint-mission"
  | "jackpot"
  | "chase"
  | "raffle";

/**
 * The selected campaign, resolved to a type + its module-owned payload. `data`
 * is opaque to everything except the owning module (which produced it in
 * `resolve` and reads it back, typed, in its builders) — so the shared shell,
 * hooks, and dispatchers never branch on concrete types.
 */
export interface CurrentCampaign {
  type: CampaignTypeId;
  /** The shared campaign shape every type exposes (image, name, status, dates). */
  campaign: CampaignSchema;
  joined: boolean;
  /** Module-owned payload — only the owning module reads it. */
  data: unknown;
}

/** One home-list / switcher entry: the shared campaign + the module's payload. */
export interface CampaignListEntry {
  campaign: CampaignSchema;
  data: unknown;
}

/** The one line a card leads with — the desktop home card's "hook": a small label
 *  ("Top prize", "Jackpots total") over the prize it points at. Shared by the home cards and the
 *  in-game campaign switcher so both surfaces sell a campaign with the same words. */
export interface CampaignHook {
  label: string;
  value: string;
  /** The value is a catalog item: star mark, never a currency symbol from its title. */
  isCatalogItem?: boolean;
}

/** Something worth switching for, shown on a campaign's cover in the in-game selector (a dot)
 *  and once as a bubble: `prize` = a won prize waiting to be revealed, `urgent` = a deadline that
 *  is now (a jackpot that must drop). `label` is the one line the bubble says. */
export interface CampaignAttention {
  tone: "urgent" | "prize";
  label: string;
}

/**
 * Equal when both resolve to the same type + campaign + join state + payload.
 * `campaign` and `data` are store references (a module's `resolve` returns the
 * stored object), so `===` is the right, cheap comparison — and it stops a fresh
 * wrapper object from re-rendering consumers when nothing underneath changed.
 */
export function currentCampaignEqual(
  a: CurrentCampaign | null,
  b: CurrentCampaign | null
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.type === b.type &&
    a.campaign === b.campaign &&
    a.joined === b.joined &&
    a.data === b.data
  );
}

/** Which home column an entry renders in (also the in-game switcher pool). */
export type CampaignListVariant = "active" | "finished";

/** How the mixed home feed asks a module to render one entry: a grid cell, or
 *  the full-width featured card used when it is the only active campaign. */
export type HomeCardLayout = "grid" | "featured";

/**
 * Live extras the in-game launcher button needs. Type-agnostic: each module
 * uses only what it needs (tournaments read rank/score; prize drops ignore them),
 * so the caller passes one uniform context.
 */
/**
 * What the overlay hands a dock panel besides the campaign. Translation, like
 * the other surfaces' builders; layout state (expanded, orientation) is the
 * dock shell's, read inside the panel with `useDockShell()`.
 */
export interface DockPanelContext {
  t: TFunction;
}

/**
 * What the in-game launcher (the coin and, later, the type's rows) shows for
 * this type. Every hook is optional: a type without it gets the launcher's
 * neutral default (no ring, …), so no type branches in the launcher itself.
 */
/** What a type's launcher content is given: which campaign, and whether
 *  motion is reduced. Its size and place come from the shell (the set's
 *  pose and the wheel's size variables on its box). */
export interface LauncherContentProps {
  campaignKey: CampaignKey;
  reduced: boolean;
  /** The coin taking a hit from the content (👺 Belmont, T3: the coin is the
   *  shell's — a type asks, the shell moves it on its own motion value). */
  coin: CoinBeat;
  /** News from the content for a screen-reader player (a task done), one
   *  line a sentence — spoken through the shell's polite live region (a
   *  region inside the control isn't reliably read; 👺 Belmont, T5). */
  announce(lines: (string | null | undefined)[]): void;
}

/** The coin's hit: `hit` squashes it in; `release` lets it recoil past its
 *  size and settle (a missions task smacked into it, then the next one
 *  thrown out). */
export interface CoinBeat {
  hit(): void;
  release(): void;
}

export interface LauncherSpec {
  /** What is running out for the coin's ring (IGB-RING-01), or null for no ring. */
  ring?(campaign: CampaignSchema, now: number): RingState | null;
  /** The type's own content, grown out from behind the coin by the
   *  launcher's shell (Sebastian, 2026-10-15: "mission content is its own
   *  component; this will be true for all types"). It draws the type's own
   *  state, and `rows` then only names the control — derived from the same
   *  view, so the two can't drift. Rows with a shared hero kind (the ask,
   *  `action`; a prize alone, `prize`) or the reveal are drawn by the shared
   *  set instead: one look for those across types. */
  Content?: ComponentType<LauncherContentProps>;
  /** The content does its own entry and exit: the shell holds the set at
   *  full size (no pop out of the coin, no slurp back in) — a prize drop's
   *  rigid pieces sliding out of the coin and snapping apart. */
  contentMoves?: boolean;
  /** The rows that grow out from behind the coin: the hero on the coin's axis
   *  (your cell, the progress pill), a row above and one below (IGB-CORE-03).
   *  Plain text for now; richer rows come with each type's pass. Must return
   *  equal values for equal inputs: the launcher compares them shallowly. */
  rows?(current: CurrentCampaign, state: AppStore, t: TFunction): LauncherRows;
  /** What the rows should say isn't known yet (the tournament: whether you
   *  opted in, before the progress stream says): the launcher holds the coin
   *  alone with its loading sweep, then grows the rows out once it is known
   *  — or, if it never comes, shows `rows` as they are (Sebastian,
   *  2026-10-15: "a pending state for the coin, then when we leave the state
   *  we transition into another one"). */
  pending?(current: CurrentCampaign, state: AppStore): boolean;
  /** The type draws its own finished state on the button. Only such types
   *  stay on it after they end (for their display window); the others leave
   *  when they end rather than sit there live-looking or bare (👺 Belmont,
   *  B5). */
  showsFinished?: boolean;
  /** The rows while a prize of yours waits to be revealed (`pending` ≥ 1 of
   *  them): the reveal set, in place of `rows` (IGB-STATE-01). Only a type
   *  whose wins are revealed on the button has it (prize drops, Sebastian:
   *  "the reveal is only for prize drops"); a type without it shows its own
   *  rows while the coin is in reveal. */
  reveal?(pending: number, t: TFunction): LauncherRows;
  /** What the reveal window's reel spins for a win here: the campaign's own
   *  droppable prize labels (never the won one — unknown until disclosed).
   *  With `reveal`: the window plays from the button without the panel
   *  (`revealContent`). */
  revealReel?(state: AppStore, key: CampaignKey): string[];
  /** The leaderboard wheel's stream (IGB-TW-07): when your rank moves by
   *  more than one place the wheel streams the rows between, so the launcher
   *  reads the players around those ranks once, at the move. `others` is the
   *  board WITHOUT you, keyed by 0-based index (the player at index i holds
   *  rank i+1 while above you, i+2 once below you); `prizes` the prize held
   *  at each rank in [lo, hi]. Read imperatively, never subscribed. */
  wheelStream?(
    current: CurrentCampaign,
    state: AppStore,
    t: TFunction,
    lo: number,
    hi: number
  ): WheelStreamData | null;
}

/** A player in the stream: the rows show the prize at each place, the
 *  player only decides whether a row is there. */
export interface WheelStreamRow {
  id: string;
  /** The player's points, drawn on the rolling row as on the side cells
   *  (D-139): the score and its unit. */
  score?: string;
  unit?: string;
}
export interface WheelStreamData {
  others: Record<number, WheelStreamRow>;
}

export interface LauncherRows {
  hero: string;
  /** `prize`: the hero is a prize title, rendered whole through `PrizeText`
   *  (never truncated; it shrinks, then glides) on the gold row. `action`:
   *  the hero IS the one thing to do — the big bright CTA (not opted in:
   *  "Opt In"). Flat strings on purpose: the launcher compares rows shallowly. */
  heroKind?: "prize" | "action" | "standing";
  /** The campaign has ended: the hero is its disabled variant (your frozen
   *  cell in a muted look — no neighbours, no hint, no beats), with the
   *  "Finished" status pill as the top row (Sebastian, 2026-10-15: "a
   *  disabled variant, just the player cell and a badge saying finished"). */
  heroDisabled?: boolean;
  /** A live note on the dark faded cell, in the finished result's size —
   *  joined with no standing yet: "Play to rank!" (Sebastian, 2026-10-19:
   *  "copy the same cell and text size as 'no win this time', but 'play to
   *  rank' instead"). */
  heroFaded?: boolean;
  /** The note's type: the progress token as a gradient with its emboss, or
   *  the prize's gold (the prize text). */
  heroTone?: "progress" | "prize";
  /** Every step of a missions campaign done: the faded cell taller, in the
   *  top prize's gold rim with its gleam, a gold check seal on its top edge
   *  (Sebastian, 2026-10-19: "only the faded cell, more height, the golden
   *  border that gleams like the tournament's first prize, the prize text
   *  for the label"). */
  heroCelebrate?: boolean;
  /** With `heroDisabled`: the hero is a prize you won — set in gold with a
   *  gold check after it on the dark faded cell (Sebastian, 2026-10-19:
   *  "use the gold prize with a gold checkmark after"). */
  heroWon?: boolean;
  /** For the slot carrying a prize: a catalog item always wears the star
   *  (never a currency mark). */
  prizeCatalog?: boolean;
  /** With `heroKind: "standing"` (your cell, IGB-TW-02): the rank on the
   *  badge. The hero text is the score; the prize held is `heroPrize`. */
  rank?: number;
  /** What the hero's number is, for the control's name ("Points 3,097"):
   *  the panel's column label, so the launcher and the panel agree. */
  heroUnit?: string;
  /** The unit drawn after a score ("pts", IGB-TW-03: muted, after the
   *  number, its own element — never part of the score string, which the
   *  name reads with `heroUnit`). Points tournaments only. */
  scoreUnit?: string;
  /** The hero's number in compact notation ("123M", with its suffix), the
   *  last step when it will not fit at the 11px floor — formatted from the
   *  number here, never parsed back from the formatted string. */
  heroCompact?: string;
  /** Your score as a number (the wheel's tick: "+N pts" rises from it). */
  heroScore?: number;
  /** With `heroKind: "standing"`: the prize held at your standing, as text
   *  in your cell after the score (IGB-TW-03: score, then prize, right-
   *  aligned) — and the leaderboard wheel's neighbours (IGB-TW-01): the
   *  cells above and below you, each drawing its rank badge and the prize
   *  that place pays (their score is spoken, not drawn), tucked behind the
   *  coin. Absent until the board has arrived, or at the ends of the board. */
  heroPrize?: string;
  heroPrizeCatalog?: boolean;
  /** The prize you hold is the tournament's top prize (its first place
   *  range, whatever the client set it to): your cell wears the crown. */
  heroTopPrize?: boolean;
  /** Where the prize you hold starts on the board (its range's first place):
   *  a lower number is a better prize — the wheel's "better prize" beat. */
  heroPrizeTier?: number;
  /** With `heroKind: "standing"` and no prize held: how far the nearest
   *  paying place is — the sentence ("3 places to a prize") is spoken, the
   *  short form ("↑3", with the prize star) is drawn in the prize's slot of
   *  your cell. Absent when you hold a prize or nothing pays above. */
  heroHint?: string;
  heroHintShort?: string;
  /** The neighbour's identity (the player hash): the cell is keyed by it, so
   *  a pass moves the row, not the node under a new player. */
  aboveId?: string;
  aboveRank?: number;
  aboveScore?: string;
  /** The score's unit ("pts", plural by the score), drawn after it on the
   *  side cell as on yours; none for a highest-win score (its "x" is in the
   *  number). */
  aboveUnit?: string;
  abovePrize?: string;
  abovePrizeCatalog?: boolean;
  belowId?: string;
  belowRank?: number;
  belowScore?: string;
  belowUnit?: string;
  belowPrize?: string;
  belowPrizeCatalog?: boolean;
  top?: string;
  /** `prize`: the top row is the prize badge — gold type with its mark, hugging
   *  the prize, riding the hero's top-right corner (the prize is priority
   *  content even when the hero is the action). `status`: a muted status
   *  pill over the hero ("Finished"), out from behind the coin. `title`: the
   *  campaign's title over its Opt In, on the dark chip, kept whole as a prize
   *  is — it shrinks to the floor, then glides (`optInRows`). */
  topKind?: "prize" | "banner" | "status" | "title";

  /** What the top row's value is, for the control's name ("Top prize €100
   *  Cash", "Prize €50 Bonus") — the module knows which, the launcher only
   *  prefixes it. */
  topLabel?: string;
  /** With `topKind: "banner"` (a type's reveal set, `LauncherSpec.reveal`):
   *  how many prizes wait, shown on the banner's end when above one. */
  topCount?: number;
  bottom?: string;
}

/**
 * One campaign type = one module. It owns how its campaign is resolved, listed,
 * and rendered across every surface, reusing the shared shell/components. Adding
 * a campaign type is: define a module + register it; the dispatchers below, the
 * shell, home, the in-game nav, and auto-select all stay untouched.
 */
export interface CampaignTypeModule {
  id: CampaignTypeId;
  /** Offered on a game-provider casino too (`isProvider`). A temporary gate
   *  (Sebastian, 2026-10-19: "can we have a temporary frontend fix for now"):
   *  providers get tournaments, missions, prize drops and raffles only — the
   *  rest stay out of every surface there (the button, the dashboard). */
  providers?: boolean;
  /** Resolve the campaign addressed by `key` into a CurrentCampaign, or null. */
  resolve(state: AppStore, key: CampaignKey): CurrentCampaign | null;
  /** Active (still running) campaigns of this type currently in the store. */
  listActive(state: AppStore): CampaignListEntry[];
  /** Finished campaigns still within their display window. */
  listFinished(state: AppStore): CampaignListEntry[];
  /** One card for the mixed active home feed. The FEED owns layout (how many per
   *  row, featured promotion); the module owns the card's type-specific content
   *  (badge label, hook line, CTA label). */
  homeCard(entry: CampaignListEntry, layout: HomeCardLayout): ReactNode;
  /** The card's hook line (label + top prize), or null when the type has none to offer yet. */
  hook(entry: CampaignListEntry, t: TFunction): CampaignHook | null;
  /** One card for the mixed finished/results grid — the compact greyed card
   *  with this type's result content (won prize / ended date). */
  finishedCard(entry: CampaignListEntry): ReactNode;
  /** This type's own reason to look right now, or null. Optional: a won prize waiting to be
   *  revealed is detected for every type in `campaignAttention`, so a type only implements this
   *  for news of its own (the jackpot's "Must Drop Now"). */
  attention?(entry: CampaignListEntry, t: TFunction): CampaignAttention | null;
  /** Full-screen detail view (shell slots), or null if `current` isn't this type. */
  detailView(current: CurrentCampaign, t: TFunction): CampaignView | null;
  /** The overlay's always-on column beside the game (`--dock-panel` wide): a
   *  narrower projection of the detail view's sections, not a second tree.
   *  Null if `current` isn't this type — or the type has no dock panel yet. */
  dockPanel(current: CurrentCampaign, ctx: DockPanelContext): ReactNode | null;
  /** The in-game launcher's per-type hooks; absent when the type has none. */
  launcher?: LauncherSpec;
}

// Registration order IS the single, intentional precedence across every surface —
// home list, in-game switcher, auto-select fallback, and the resolve tie-break —
// so a campaign's position is consistent everywhere (no per-surface ordering).
// `@/campaigns/index.ts` registers `tournament` before `prizedrop`, matching the
// tournament-first home/switcher order the widget has always shown. (This unifies
// the old code's one inconsistency: its auto-select fallback happened to prefer
// prize drops while the visible lists were tournament-first.)
// The one thing that outranks it is the operator's "Display in first position"
// flag (`is_priority`) — see `collectCampaignEntries`.
const modules = new Map<CampaignTypeId, CampaignTypeModule>();

export function registerCampaignType(module: CampaignTypeModule): void {
  modules.set(module.id, module);
}

export function getCampaignModule(
  id: CampaignTypeId
): CampaignTypeModule | undefined {
  return modules.get(id);
}

export function listCampaignModules(): CampaignTypeModule[] {
  return [...modules.values()];
}

/** The modules this casino is offered: on a game provider only those that
 *  say so (`providers`), in registration order. */
function offeredModules(state: AppStore): CampaignTypeModule[] {
  const all = [...modules.values()];
  return state.isProvider ? all.filter((module) => module.providers) : all;
}

/** Resolve the campaign addressed by `key` to whichever module owns it. A given
 *  campaign is a single row of a single type, so exactly one module matches in
 *  practice; on the unusual event of a cross-type `custom_id` collision, the
 *  first module in registration order (tournament) wins — consistent with the
 *  precedence used everywhere else. */
export function resolveCurrentCampaign(
  state: AppStore,
  key: CampaignKey
): CurrentCampaign | null {
  if (!key) return null;
  for (const module of offeredModules(state)) {
    const current = module.resolve(state, key);
    if (current) return current;
  }
  return null;
}

/** The ask, in every type (Sebastian, 2026-10-22: "for the not opted in
 *  state we now show the prize chip and the Opt In button — replace the prize
 *  chip with the campaign title, and use the same logic as we do for prizes
 *  with scaling to a limit, then scrolls like a marquee"): Opt In as the
 *  hero, the campaign's title over it. */
export function optInRows(
  campaign: CampaignSchema,
  t: TFunction
): LauncherRows {
  return {
    hero: t("campaign.optIn"),
    heroKind: "action",
    top: campaignDisplayTitle(campaign, t),
    topKind: "title",
  };
}

/** The reveal window's content for `key` — its campaign and the reel the
 *  owning module spins — or null when no module reveals wins there. */
export function revealContent(
  state: AppStore,
  key: CampaignKey
): { campaign: CampaignSchema; prizes: string[] } | null {
  for (const module of offeredModules(state)) {
    const current = module.resolve(state, key);
    if (!current) continue;
    const prizes = module.launcher?.revealReel?.(state, key);
    return prizes ? { campaign: current.campaign, prizes } : null;
  }
  return null;
}

/** One entry, tagged with the module that owns it (so the list can render it). */
export interface OwnedEntry {
  module: CampaignTypeModule;
  entry: CampaignListEntry;
}

/** Operator-set "Display in first position" (`Campaign.is_priority`). Read
 *  leniently: a payload rendered before the flag shipped simply has no field. */
const displayFirst = (campaign: CampaignSchema): boolean =>
  campaign.is_priority === true;

/**
 * All entries of a variant across every registered type, in registration order —
 * except that ACTIVE campaigns the operator flagged "Display in first position"
 * move to the front (stable sort, so flagged and unflagged each keep the
 * registration order among themselves). This is the pool the in-game launcher
 * button, the switcher and auto-select pick their default from, so the flag lands
 * on all three at once. Finished campaigns never carry it: the backend clears
 * `is_priority` when a campaign finishes.
 */
/** A switcher entry's news, if any: a won prize waiting to be revealed (any type), else the
 *  owning module's own signal. Finished campaigns have none. */
export function campaignAttention(
  state: AppStore,
  owned: OwnedEntry,
  t: TFunction
): CampaignAttention | null {
  const { campaign } = owned.entry;
  if (campaign.status === "FI") return null;
  if (campaignKey(campaign) in state.unrevealedWins) {
    return { tone: "prize", label: t("igwButton.youHaveWon", "You have won!") };
  }
  return owned.module.attention?.(owned.entry, t) ?? null;
}

/** The hook line for a switcher / home entry, via its owning module. */
export function campaignHook(
  owned: OwnedEntry,
  t: TFunction
): CampaignHook | null {
  return owned.module.hook(owned.entry, t);
}

export function collectCampaignEntries(
  state: AppStore,
  variant: CampaignListVariant
): OwnedEntry[] {
  const entries = offeredModules(state).flatMap((module) =>
    (variant === "active"
      ? module.listActive(state)
      : module.listFinished(state)
    ).map((entry) => ({ module, entry }))
  );
  if (variant !== "active") return entries;
  return entries.sort(
    (a, b) =>
      Number(displayFirst(b.entry.campaign)) -
      Number(displayFirst(a.entry.campaign))
  );
}

/**
 * The mixed active home feed: every type's active campaigns, grouped by type in
 * registration order, ending-soonest first within each type. Kept separate from
 * `collectCampaignEntries` so the switcher/auto-select order stays untouched.
 */
export function collectHomeFeedEntries(state: AppStore): OwnedEntry[] {
  return offeredModules(state).flatMap((module) =>
    module
      .listActive(state)
      .map((entry) => ({ module, entry }))
      .sort(
        (a, b) =>
          new Date(a.entry.campaign.end).getTime() -
          new Date(b.entry.campaign.end).getTime()
      )
  );
}

/**
 * Finished campaigns still inside their display window — the subset the in-game
 * surfaces (dropdown switcher, launcher button, auto-select) show. The results
 * grid in the open widget deliberately does NOT use this: it shows every finished
 * campaign the stream served, which is the last N to end plus anything still
 * in-window. See `isWithinDisplayWindow`.
 */
export function collectInWindowFinishedEntries(state: AppStore): OwnedEntry[] {
  return collectCampaignEntries(state, "finished").filter(({ entry }) =>
    isWithinDisplayWindow(entry.campaign)
  );
}

/**
 * The mixed finished/results grid: every finished campaign the stream served,
 * most-recently-ended first ACROSS types — not grouped by type like the active
 * feed, because this list reads as one chronological "your last results" history
 * and the backend's cap on it is global across campaign types too.
 */
export function collectFinishedFeedEntries(state: AppStore): OwnedEntry[] {
  return collectCampaignEntries(state, "finished").sort(
    (a, b) =>
      new Date(b.entry.campaign.end).getTime() -
      new Date(a.entry.campaign.end).getTime()
  );
}

/**
 * Element-wise equality for feed selections. Entries are store references (see
 * `currentCampaignEqual`), so `===` per field is correct and cheap — this keeps
 * a `useStoreWithEqualityFn(collectHomeFeedEntries)` from re-rendering on every
 * store write (the collector allocates a fresh array each call).
 */
export function ownedEntriesEqual(a: OwnedEntry[], b: OwnedEntry[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every(
    (x, i) =>
      x.module === b[i].module &&
      x.entry.campaign === b[i].entry.campaign &&
      x.entry.data === b[i].entry.data
  );
}

/**
 * First campaign (actives first, then finished, in the shared
 * `collectCampaignEntries` order — so a "display first" active campaign wins a
 * simultaneous-win tie-break over registration order, deliberately: the flag is
 * the one thing that outranks type order everywhere, and both wins still
 * surface, one after the other) holding an unrevealed prize the registry can
 * resolve — undefined when no win is waiting. Resolvability is checked so an
 * entry whose type-specific data hasn't landed yet can't blank a caller that
 * renders the result.
 */
export function unrevealedWinCampaignKey(
  state: AppStore
): CampaignKey | undefined {
  const withWin = [
    ...collectCampaignEntries(state, "active"),
    ...collectCampaignEntries(state, "finished"),
  ].find(({ entry }) => {
    const key = campaignKey(entry.campaign);
    return (
      key in state.unrevealedWins && resolveCurrentCampaign(state, key) !== null
    );
  });
  return withWin ? campaignKey(withWin.entry.campaign) : undefined;
}

/**
 * The campaign the widget / auto-select falls back to when nothing is
 * explicitly selected: a campaign with a pending prize reveal first (a win on
 * a finished or non-first campaign could otherwise never be confirmed — and
 * its notification never acked), else the first active campaign across all
 * types, else the first finished one still in its display window.
 * Type-agnostic — the module order defines precedence.
 */
export function resolveEffectiveCampaignKey(
  state: AppStore,
  selectedKey: string | null
): CampaignKey | undefined {
  if (selectedKey) return selectedKey;
  const winKey = unrevealedWinCampaignKey(state);
  if (winKey) return winKey;
  const active = collectCampaignEntries(state, "active")[0];
  if (active) return campaignKey(active.entry.campaign);
  // In-window only: the stream also carries older finished campaigns for the
  // results grid, but the launcher must not fall back to a weeks-old result.
  const finished = collectInWindowFinishedEntries(state)[0];
  return finished ? campaignKey(finished.entry.campaign) : undefined;
}

/**
 * The campaign the IN-GAME launcher button shows. Product priority:
 * prize reveal > active campaign > finished campaign — an unrevealed prize
 * outranks even the player's explicit selection, so the reveal button always
 * surfaces while a win is waiting; once every prize is confirmed the pick
 * falls back to the selection (the player can switch however they want), then
 * the shared default order.
 */
export function resolveInGameButtonKey(
  state: AppStore,
  selectedKey: string | null
): CampaignKey | undefined {
  return (
    unrevealedWinCampaignKey(state) ??
    resolveEffectiveCampaignKey(state, selectedKey)
  );
}

/** Dispatch the current campaign to its type's detail-view builder. */
export function buildCampaignView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  return getCampaignModule(current.type)?.detailView(current, t) ?? null;
}

/** Dispatch the current campaign to its type's dock panel. */
export function buildDockPanel(
  current: CurrentCampaign,
  ctx: DockPanelContext
): ReactNode | null {
  return getCampaignModule(current.type)?.dockPanel(current, ctx) ?? null;
}
